import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { apiService } from '../../../../../services/api';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    OrcamentoDetalhado,
    CreateOrcamentoData,
    UpdateOrcamentoData,
    StatusOrcamento,
    CreateOrcamentoProdutoData,
    OrcamentoProdutoDetalhado
} from '../../../../../types/orcamentos_multiplos';
import { ProdutoOportunidade } from '../../../../../types/orcamento';
import { Fornecedor } from '../../../../../types/fornecedor';
import QuickFornecedorForm from '../QuickFornecedorForm';
import { calcularMargem, nivelDaMargem } from '../../../../../utils/margem';

interface ModalNovoOrcamentoProps {
    show: boolean;
    onHide: () => void;
    opportunityId: number;
    orcamento?: OrcamentoDetalhado | null;
    onSave: () => void;
    formatCurrency: (value: number) => string;
}

interface ItemOrcamento {
    id_produto_oportunidade: number;
    produto_nome: string;
    quantidade: number;
    valor_unitario: number;
    unidade_medida: string;
    numero_edital?: string;
    marca?: string;
    descricao?: string;
    observacoes?: string;
}

interface FormData {
    titulo: string;
    fornecedor_id: number | null;
    status: StatusOrcamento;
    data_validade: Date | null;
    descricao: string;
    observacoes: string;
    itens: ItemOrcamento[];
    valor_total_manual: string;
    usar_valor_manual: boolean;
}

const statusOptions: StatusOrcamento[] = [
    'Rascunho',
    'Em Análise',
    'Aprovado',
    'Rejeitado',
    'Vencedor',
    'Cancelado'
];

const ModalNovoOrcamento: React.FC<ModalNovoOrcamentoProps> = ({
    show,
    onHide,
    opportunityId,
    orcamento,
    onSave,
    formatCurrency
}) => {
    const [formData, setFormData] = useState<FormData>({
        titulo: '',
        fornecedor_id: null,
        status: 'Em Análise',
        data_validade: new Date(),
        descricao: '',
        observacoes: '',
        itens: [],
        valor_total_manual: '0,0000',
        usar_valor_manual: false
    });

    // Bloco único da Calculadora de Margem (strings para permitir campo vazio)
    const [custos, setCustos] = useState({
        custo_logistico: '0',
        custos_adicionais: '0',
        percentual_imposto: '0'
    });
    const [impostoInvalido, setImpostoInvalido] = useState(false);

    const [produtosOportunidade, setProdutosOportunidade] = useState<ProdutoOportunidade[]>([]);
    const [produtosOrdenados, setProdutosOrdenados] = useState<ProdutoOportunidade[]>([]);
    const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Estados para o select de fornecedores com pesquisa
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    // Estado para controlar o formulário inline de fornecedor
    const [showQuickFornecedorForm, setShowQuickFornecedorForm] = useState(false);

    // Função para ordenar produtos por número de edital (mesma lógica do OrcamentoList)
    const ordenarProdutosPorEdital = (produtosParaOrdenar: ProdutoOportunidade[]): ProdutoOportunidade[] => {
        const getNumeroEditalFromString = (edital: string | null | undefined): number => {
            if (!edital || typeof edital !== 'string' || edital.trim() === '') {
                return 0;
            }
            const match = edital.match(/\d+/);
            if (!match || !match[0]) {
                return 0;
            }
            const parsedNum = parseInt(match[0], 10);
            return isNaN(parsedNum) ? 0 : parsedNum;
        };

        return [...produtosParaOrdenar].sort((a, b) => {
            const numeroEditalA = getNumeroEditalFromString(a.numero_edital);
            const numeroEditalB = getNumeroEditalFromString(b.numero_edital);

            const aTemEditalValido = numeroEditalA !== 0;
            const bTemEditalValido = numeroEditalB !== 0;

            if (aTemEditalValido && !bTemEditalValido) {
                return -1;
            }
            if (!aTemEditalValido && bTemEditalValido) {
                return 1;
            }

            if (aTemEditalValido && bTemEditalValido) {
                if (numeroEditalA !== numeroEditalB) {
                    return numeroEditalA - numeroEditalB;
                }
                return a.id - b.id;
            }

            return a.id - b.id;
        });
    };

    // Carregar dados ao abrir o modal
    useEffect(() => {
        if (show && opportunityId) {
            loadDados();
        }
    }, [show, opportunityId]);

    // Fechar dropdown de pesquisa ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsSearchOpen(false);
            }
        };

        if (isSearchOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSearchOpen]);

    // Se está editando, carregar dados do orçamento
    useEffect(() => {
        if (show && orcamento) {
            loadDadosEdicao();
        } else if (show && !orcamento) {
            // Resetar formulário para novo orçamento
            resetForm();
        }
    }, [show, orcamento]);

    const loadDados = async () => {
        try {
            setIsLoading(true);

            // Carregar produtos da oportunidade
            const produtosRes = await apiService.getProdutosOportunidade(opportunityId);
            if (produtosRes.success && Array.isArray(produtosRes.data)) {
                // Ordenar produtos por N° Edital
                const produtosOrdenadosArray = ordenarProdutosPorEdital(produtosRes.data);
                setProdutosOportunidade(produtosRes.data);
                setProdutosOrdenados(produtosOrdenadosArray);

                // Sugere os custos já cadastrados no primeiro produto (o usuário pode alterar)
                const produtoBase = produtosOrdenadosArray[0];
                if (produtoBase) {
                    setCustos({
                        custo_logistico: String(produtoBase.custo_logistico ?? 0),
                        custos_adicionais: String(produtoBase.custos_adicionais ?? 0),
                        percentual_imposto: String(produtoBase.percentual_imposto ?? 0)
                    });
                }

                // Se é novo orçamento e não tem itens, adicionar o primeiro produto (ordenado por edital)
                if (!orcamento && formData.itens.length === 0 && produtosOrdenadosArray.length > 0) {
                    const primeiroProduto = produtosOrdenadosArray[0];
                    setFormData(prev => ({
                        ...prev,
                        itens: [{
                            id_produto_oportunidade: primeiroProduto.id,
                            produto_nome: primeiroProduto.nome_produto,
                            quantidade: primeiroProduto.quantidade,
                            valor_unitario: primeiroProduto.valor_orcado || 0,
                            unidade_medida: primeiroProduto.unidade_medida,
                            numero_edital: primeiroProduto.numero_edital || undefined,
                            marca: '',
                            descricao: '',
                            observacoes: ''
                        }]
                    }));
                }
            }

            // Carregar fornecedores
            const fornecedoresRes = await apiService.getFornecedores({ tipo_entidade: 'PJ' });
            if (fornecedoresRes.success) {
                setFornecedores(fornecedoresRes.data);
            }

        } catch (error: any) {
            console.error('Erro ao carregar dados:', error);
            toast.error(error.message || 'Erro ao carregar dados');
        } finally {
            setIsLoading(false);
        }
    };

    const loadDadosEdicao = async () => {
        if (!orcamento) return;

        try {
            setIsLoading(true);

            // Carregar produtos do orçamento
            const produtosOrcRes = await apiService.getOrcamentoProdutos({
                id_orcamento: orcamento.id
            });

            let itens: ItemOrcamento[] = [];
            if ('data' in produtosOrcRes && Array.isArray(produtosOrcRes.data)) {
                itens = produtosOrcRes.data.map((prod: OrcamentoProdutoDetalhado) => {
                    // Buscar o produto na lista de produtos da oportunidade para pegar o numero_edital
                    const produtoOportunidade = produtosOportunidade.find(p => p.id === prod.id_produto_oportunidade);

                    return {
                        id_produto_oportunidade: prod.id_produto_oportunidade,
                        produto_nome: prod.produto_nome,
                        quantidade: parseFloat(prod.quantidade),
                        valor_unitario: parseFloat(prod.valor_unitario),
                        unidade_medida: prod.unidade_medida,
                        numero_edital: produtoOportunidade?.numero_edital || undefined,
                        marca: prod.marca || '',
                        descricao: prod.descricao || '',
                        observacoes: prod.observacoes || ''
                    };
                });
            }

            setFormData({
                titulo: orcamento.titulo,
                fornecedor_id: orcamento.fornecedor_id,
                status: orcamento.status,
                data_validade: orcamento.data_validade ? new Date(orcamento.data_validade) : null,
                descricao: orcamento.descricao || '',
                observacoes: orcamento.observacoes || '',
                itens: itens,
                valor_total_manual: parseFloat(orcamento.valor_total).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4
                }),
                usar_valor_manual: false
            });

            // Preencher campo de pesquisa com nome do fornecedor
            if (orcamento.fornecedor_nome) {
                setSearchTerm(orcamento.fornecedor_nome);
            }

        } catch (error: any) {
            console.error('Erro ao carregar dados de edição:', error);
            toast.error(error.message || 'Erro ao carregar dados do orçamento');
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            titulo: '',
            fornecedor_id: null,
            status: 'Em Análise',
            data_validade: new Date(),
            descricao: '',
            observacoes: '',
            itens: [],
            valor_total_manual: '0,0000',
            usar_valor_manual: false
        });
        setSearchTerm('');
        setImpostoInvalido(false);
        setCustos({ custo_logistico: '0', custos_adicionais: '0', percentual_imposto: '0' });
    };

    // Cor do badge de margem (mesmas faixas do restante do sistema)
    const margemBadgeClass = (margem: number): string => {
        const nivel = nivelDaMargem(margem);
        if (nivel === 'baixa') return 'bg-danger';
        if (nivel === 'mediana') return 'bg-warning';
        return 'bg-success';
    };

    // Margem ao vivo de cada item: referência x valor deste orçamento (proposta = orçado)
    const margensPorItem = formData.itens.map((item, index) => {
        const produto = produtosOportunidade.find(p => p.id === item.id_produto_oportunidade);
        const margem = calcularMargem({
            valorProposta: item.valor_unitario,
            valorReferencia: Number(produto?.valor_referencia ?? 0),
            valorOrcado: item.valor_unitario,
            custoLogistico: parseFloat(custos.custo_logistico) || 0,
            custosAdicionais: parseFloat(custos.custos_adicionais) || 0,
            percentualImposto: parseFloat(custos.percentual_imposto) || 0
        });
        return { index, nome: item.produto_nome, margem };
    });

    // Depois de salvar os itens, aplica os custos do bloco aos produtos em que este
    // orçamento ficou como o menor valor (a margem é recalculada pelo backend).
    const aplicarCustosNosProdutosMenores = async () => {
        try {
            const res = await apiService.getProdutosOportunidade(opportunityId);
            if (!res.success || !Array.isArray(res.data)) return;

            const custosNumericos = {
                custo_logistico: parseFloat(custos.custo_logistico) || 0,
                custos_adicionais: parseFloat(custos.custos_adicionais) || 0,
                percentual_imposto: parseFloat(custos.percentual_imposto) || 0
            };

            for (const item of formData.itens) {
                const po = res.data.find((p: ProdutoOportunidade) => p.id === item.id_produto_oportunidade);
                if (!po) continue;

                // Este item é o menor se o produto ficou com o valor e o fornecedor deste orçamento
                const mesmoValor = Math.abs(Number(po.valor_orcado ?? 0) - item.valor_unitario) < 0.00005;
                const mesmoFornecedor = Number(po.fornecedor_id ?? 0) === Number(formData.fornecedor_id ?? 0);
                if (!mesmoValor || !mesmoFornecedor) continue;

                await apiService.updateProdutoOportunidade(po.id, custosNumericos);
            }
        } catch (error) {
            console.error('Erro ao aplicar custos aos produtos:', error);
            toast.warn('Orçamento salvo, mas não foi possível aplicar os custos ao produto');
        }
    };

    // Filtrar fornecedores
    const filteredFornecedores = fornecedores.filter(fornecedor => {
        const searchLower = searchTerm.toLowerCase();
        return (
            fornecedor.nome.toLowerCase().includes(searchLower) ||
            fornecedor.razao_social?.toLowerCase().includes(searchLower) ||
            fornecedor.nome_fantasia?.toLowerCase().includes(searchLower)
        );
    });

    const handleSelectFornecedor = (fornecedor: Fornecedor) => {
        setFormData(prev => ({
            ...prev,
            fornecedor_id: fornecedor.id
        }));

        // Montar nome para exibição
        const nomeExibicao = fornecedor.tipo_entidade === 'PJ'
            ? `${fornecedor.razao_social}${fornecedor.nome_fantasia ? ` (${fornecedor.nome_fantasia})` : ''}`
            : fornecedor.nome;

        setSearchTerm(nomeExibicao);
        setIsSearchOpen(false);
    };

    const handleLimparFornecedor = () => {
        setFormData(prev => ({ ...prev, fornecedor_id: null }));
        setSearchTerm('');
    };

    // Handler para quando um fornecedor é criado via formulário rápido
    const handleQuickFornecedorCreated = async (newFornecedor: Fornecedor) => {
        try {
            // 1. Adicionar à lista de fornecedores de forma otimista
            setFornecedores(prev => [...prev, newFornecedor]);

            // 2. Fechar o formulário inline imediatamente
            setShowQuickFornecedorForm(false);

            // 3. Selecionar automaticamente o novo fornecedor
            setFormData(prev => ({
                ...prev,
                fornecedor_id: newFornecedor.id
            }));

            // 4. Montar nome para exibição no campo de pesquisa
            const nomeExibicao = newFornecedor.tipo_entidade === 'PJ'
                ? `${newFornecedor.razao_social}${newFornecedor.nome_fantasia ? ` (${newFornecedor.nome_fantasia})` : ''}`
                : newFornecedor.nome;

            setSearchTerm(nomeExibicao);

            // 5. Feedback visual sucinto
            toast.success('✓ Fornecedor cadastrado e selecionado', {
                autoClose: 2000,
                position: 'top-right'
            });

            // 6. Recarregar lista completa em background (sincronização)
            apiService.getFornecedores({ tipo_entidade: 'PJ' }).then(fornecedoresRes => {
                if (fornecedoresRes.success) {
                    setFornecedores(fornecedoresRes.data);
                }
            }).catch(err => {
                console.warn('Erro ao sincronizar lista de fornecedores:', err);
            });

        } catch (error) {
            console.error('Erro ao processar novo fornecedor:', error);
            toast.error('Erro ao selecionar o fornecedor', {
                autoClose: 3000
            });
            setShowQuickFornecedorForm(false);
        }
    };

    // Adicionar novo item - adiciona o próximo produto da lista ordenada por N° Edital
    const adicionarItem = () => {
        if (produtosOrdenados.length === 0) {
            toast.warning('Nenhum produto disponível na oportunidade');
            return;
        }

        // IDs dos produtos já adicionados
        const idsAdicionados = formData.itens.map(item => item.id_produto_oportunidade);

        // Encontrar o próximo produto não adicionado (baseado na ordenação por edital)
        const proximoProduto = produtosOrdenados.find(prod => !idsAdicionados.includes(prod.id));

        if (!proximoProduto) {
            toast.info('Todos os produtos já foram adicionados ao orçamento');
            return;
        }

        const novoItem: ItemOrcamento = {
            id_produto_oportunidade: proximoProduto.id,
            produto_nome: proximoProduto.nome_produto,
            quantidade: proximoProduto.quantidade,
            valor_unitario: proximoProduto.valor_orcado || 0,
            unidade_medida: proximoProduto.unidade_medida,
            numero_edital: proximoProduto.numero_edital || undefined,
            marca: '',
            descricao: '',
            observacoes: ''
        };

        setFormData(prev => ({
            ...prev,
            itens: [...prev.itens, novoItem]
        }));
    };

    // Remover item
    const removerItem = (index: number) => {
        setFormData(prev => ({
            ...prev,
            itens: prev.itens.filter((_, i) => i !== index)
        }));
    };

    // Alterar item
    const alterarItem = (index: number, campo: keyof ItemOrcamento, valor: any) => {
        setFormData(prev => {
            const novosItens = [...prev.itens];

            if (campo === 'id_produto_oportunidade') {
                // Quando muda o produto, atualizar todos os dados e resetar marca/descrição
                const produto = produtosOportunidade.find(p => p.id === parseInt(valor));
                if (produto) {
                    novosItens[index] = {
                        ...novosItens[index],
                        id_produto_oportunidade: produto.id,
                        produto_nome: produto.nome_produto,
                        quantidade: produto.quantidade,
                        valor_unitario: produto.valor_orcado || 0,
                        unidade_medida: produto.unidade_medida,
                        numero_edital: produto.numero_edital || undefined,
                        marca: '', // Resetar marca
                        descricao: '' // Resetar descrição
                    };
                }
            } else {
                novosItens[index] = {
                    ...novosItens[index],
                    [campo]: valor
                };
            }

            return { ...prev, itens: novosItens };
        });
    };

    // Calcular valor total
    const calcularValorTotal = (): number => {
        if (formData.usar_valor_manual) {
            return parseFloat(formData.valor_total_manual.replace(',', '.')) || 0;
        }

        return formData.itens.reduce((total, item) => {
            return total + (item.quantidade * item.valor_unitario);
        }, 0);
    };

    // Calcular subtotal de um item
    const calcularSubtotal = (item: ItemOrcamento): number => {
        return item.quantidade * item.valor_unitario;
    };

    const handleValorTotalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const valor = e.target.value.replace(/[^\d,]/g, '');
        setFormData(prev => ({
            ...prev,
            valor_total_manual: valor
        }));
    };

    // Validar e submeter
    const handleSubmit = async () => {
        try {
            // Validações
            if (!formData.titulo.trim()) {
                toast.error('Título do orçamento é obrigatório');
                return;
            }

            if (formData.itens.length === 0) {
                toast.error('Adicione pelo menos um produto ao orçamento');
                return;
            }

            const valorTotal = calcularValorTotal();
            if (valorTotal <= 0) {
                toast.error('Valor total deve ser maior que zero');
                return;
            }

            // Imposto obrigatório (mesma regra da edição do produto)
            if ((parseFloat(custos.percentual_imposto) || 0) <= 0) {
                setImpostoInvalido(true);
                toast.error('O percentual de imposto é obrigatório');
                return;
            }
            setImpostoInvalido(false);

            // Validar cada item
            for (const item of formData.itens) {
                if (item.quantidade <= 0) {
                    toast.error('Quantidade deve ser maior que zero');
                    return;
                }
                if (item.valor_unitario < 0) {
                    toast.error('Valor unitário não pode ser negativo');
                    return;
                }
            }

            setIsSubmitting(true);

            if (orcamento) {
                // MODO EDIÇÃO
                await handleUpdate();
            } else {
                // MODO CRIAÇÃO
                await handleCreate();
            }

        } catch (error: any) {
            console.error('Erro ao salvar orçamento:', error);
            toast.error(error.message || 'Erro ao salvar orçamento');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreate = async () => {
        // 1. Criar orçamento
        const dadosOrcamento: CreateOrcamentoData = {
            id_oportunidade: opportunityId,
            fornecedor_id: formData.fornecedor_id || undefined,
            titulo: formData.titulo,
            descricao: formData.descricao || undefined,
            status: formData.status,
            data_validade: formData.data_validade?.toISOString().split('T')[0],
            observacoes: formData.observacoes || undefined
        };

        const resultOrcamento = await apiService.createOrcamento(dadosOrcamento);

        if (!resultOrcamento.success) {
            throw new Error(resultOrcamento.message || 'Erro ao criar orçamento');
        }

        const orcamentoId = resultOrcamento.data.id;

        // 2. Adicionar produtos
        for (const item of formData.itens) {
            const dadosProduto: CreateOrcamentoProdutoData = {
                id_orcamento: orcamentoId,
                id_produto_oportunidade: item.id_produto_oportunidade,
                quantidade: item.quantidade,
                valor_unitario: item.valor_unitario,
                marca: item.marca || undefined,
                descricao: item.descricao || undefined,
                observacoes: item.observacoes || undefined
            };

            await apiService.createOrcamentoProduto(dadosProduto);
        }

        // Aplica custos/imposto aos produtos em que este orçamento virou o menor
        await aplicarCustosNosProdutosMenores();

        toast.success(`Orçamento ${resultOrcamento.data.numero_orcamento} criado com sucesso!`);
        onSave();
        onHide();
        resetForm();
    };

    const handleUpdate = async () => {
        if (!orcamento) return;

        // 1. Atualizar cabeçalho do orçamento
        const dadosAtualizacao: UpdateOrcamentoData = {
            fornecedor_id: formData.fornecedor_id || undefined,
            titulo: formData.titulo,
            descricao: formData.descricao || undefined,
            status: formData.status,
            data_validade: formData.data_validade?.toISOString().split('T')[0],
            observacoes: formData.observacoes || undefined
        };

        await apiService.updateOrcamento(orcamento.id, dadosAtualizacao);

        // 2. Atualizar produtos (simplificado: deletar todos e recriar)
        // Em produção, seria melhor comparar e fazer update/delete/create seletivo
        const produtosAtuais = await apiService.getOrcamentoProdutos({
            id_orcamento: orcamento.id
        });

        if ('data' in produtosAtuais && Array.isArray(produtosAtuais.data)) {
            for (const prod of produtosAtuais.data) {
                await apiService.deleteOrcamentoProduto(prod.id);
            }
        }

        // Recriar produtos
        for (const item of formData.itens) {
            const dadosProduto: CreateOrcamentoProdutoData = {
                id_orcamento: orcamento.id,
                id_produto_oportunidade: item.id_produto_oportunidade,
                quantidade: item.quantidade,
                valor_unitario: item.valor_unitario,
                marca: item.marca || undefined,
                descricao: item.descricao || undefined,
                observacoes: item.observacoes || undefined
            };

            await apiService.createOrcamentoProduto(dadosProduto);
        }

        // Aplica custos/imposto aos produtos em que este orçamento é o menor
        await aplicarCustosNosProdutosMenores();

        toast.success('Orçamento atualizado com sucesso!');
        onSave();
        onHide();
    };

    return (
        <Modal
            show={show}
            onHide={() => !isSubmitting && onHide()}
            size="lg"
            backdrop="static"
            keyboard={!isSubmitting}
        >
            <Modal.Header closeButton={!isSubmitting}>
                <Modal.Title>
                    {orcamento ? 'Editar Orçamento' : 'Novo Orçamento'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {isLoading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                        <p className="text-muted mt-2">Carregando dados...</p>
                    </div>
                ) : (
                    <>
                        {/* Seção 1: Dados do Cabeçalho */}
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <label className="form-label">
                                    Título do Orçamento <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.titulo}
                                    onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                                    placeholder="Ex: Orçamento Fornecedor ABC"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {/* Select de Fornecedor com Pesquisa */}
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <label className="form-label">Fornecedor</label>
                                <div className="position-relative" ref={searchRef}>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Pesquisar fornecedor..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setFormData(prev => ({ ...prev, fornecedor_id: null }));
                                                setIsSearchOpen(true);
                                            }}
                                            onFocus={() => setIsSearchOpen(true)}
                                            disabled={isSubmitting}
                                        />
                                        {formData.fornecedor_id && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={handleLimparFornecedor}
                                                disabled={isSubmitting}
                                            >
                                                <i className="ti ti-x" />
                                            </button>
                                        )}
                                    </div>

                                    {isSearchOpen && !isSubmitting && (
                                        <div
                                            className="position-absolute start-0 w-100 mt-1 bg-white shadow-sm border rounded"
                                            style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}
                                        >
                                            {filteredFornecedores.length === 0 ? (
                                                <div className="p-2 text-muted">Nenhum fornecedor encontrado</div>
                                            ) : (
                                                filteredFornecedores.map(fornecedor => (
                                                    <div
                                                        key={fornecedor.id}
                                                        className="p-2 cursor-pointer hover-bg-light"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSelectFornecedor(fornecedor)}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                    >
                                                        {fornecedor.tipo_entidade === 'PJ' ? (
                                                            <>
                                                                <div className="fw-semibold">{fornecedor.razao_social}</div>
                                                                {fornecedor.nome_fantasia && (
                                                                    <small className="text-muted">
                                                                        {fornecedor.nome_fantasia}
                                                                    </small>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div className="fw-semibold">{fornecedor.nome}</div>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Botão de Cadastro Rápido */}
                                {!showQuickFornecedorForm && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary mt-2 w-100"
                                        onClick={() => setShowQuickFornecedorForm(true)}
                                        disabled={isSubmitting}
                                    >
                                        <i className="ti ti-plus me-1"></i>
                                        Cadastrar novo fornecedor
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Formulário Inline de Cadastro Rápido de Fornecedor */}
                        {showQuickFornecedorForm && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="card border-primary quick-fornecedor-card">
                                        <div className="card-body">
                                            <QuickFornecedorForm
                                                onSuccess={handleQuickFornecedorCreated}
                                                onCancel={() => setShowQuickFornecedorForm(false)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Produtos */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label mb-0">
                                    Produtos <span className="text-danger">*</span>
                                </label>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={adicionarItem}
                                    disabled={isSubmitting || produtosOportunidade.length === 0}
                                >
                                    <i className="ti ti-plus me-1" />
                                    Adicionar Produto
                                </button>
                            </div>

                            {formData.itens.length === 0 ? (
                                <div className="alert alert-info">
                                    <i className="ti ti-info-circle me-2" />
                                    Adicione produtos ao orçamento
                                </div>
                            ) : (
                                <div className="border rounded p-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {formData.itens.map((item, index) => (
                                        <div key={index} className="card mb-2 border">
                                            <div className="card-body p-2">
                                                <div className="row g-2">
                                                    {/* N° Edital - Badge visual */}
                                                    {item.numero_edital && (
                                                        <div className="col-12">
                                                            <span className="badge bg-info text-white">
                                                                <i className="ti ti-file-text me-1" />
                                                                Edital: {item.numero_edital}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Produto Select */}
                                                    <div className="col-md-5">
                                                        <label className="form-label small">Produto</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={item.id_produto_oportunidade}
                                                            onChange={(e) => alterarItem(index, 'id_produto_oportunidade', e.target.value)}
                                                            disabled={isSubmitting}
                                                        >
                                                            {produtosOrdenados.map(prod => (
                                                                <option key={prod.id} value={prod.id}>
                                                                    {prod.numero_edital ? `[${prod.numero_edital}] ` : ''}{prod.nome_produto} ({prod.quantidade} {prod.unidade_medida})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Quantidade */}
                                                    <div className="col-md-2">
                                                        <label className="form-label small">Quantidade</label>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            step="0.01"
                                                            min="0.01"
                                                            value={item.quantidade}
                                                            onChange={(e) => alterarItem(index, 'quantidade', parseFloat(e.target.value) || 0)}
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>

                                                    {/* Valor Unitário */}
                                                    <div className="col-md-2">
                                                        <label className="form-label small">Val. Unit.</label>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            step="0.0001"
                                                            min="0"
                                                            value={item.valor_unitario}
                                                            onChange={(e) => alterarItem(index, 'valor_unitario', parseFloat(e.target.value) || 0)}
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>

                                                    {/* Botão Remover */}
                                                    <div className="col-md-3 d-flex align-items-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger w-100"
                                                            onClick={() => removerItem(index)}
                                                            disabled={isSubmitting}
                                                        >
                                                            <i className="ti ti-trash me-1" />
                                                            Remover
                                                        </button>
                                                    </div>

                                                    {/* Marca */}
                                                    <div className="col-md-6">
                                                        <label className="form-label small">Marca do Fornecedor</label>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            placeholder="Ex: Marca XYZ"
                                                            value={item.marca || ''}
                                                            onChange={(e) => alterarItem(index, 'marca', e.target.value)}
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>

                                                    {/* Descrição */}
                                                    <div className="col-md-6">
                                                        <label className="form-label small">Descrição Específica</label>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            placeholder="Ex: Produto premium com..."
                                                            value={item.descricao || ''}
                                                            onChange={(e) => alterarItem(index, 'descricao', e.target.value)}
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>

                                                    {/* Observações */}
                                                    <div className="col-md-8">
                                                        <label className="form-label small">Observações</label>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            placeholder="Observações do item"
                                                            value={item.observacoes || ''}
                                                            onChange={(e) => alterarItem(index, 'observacoes', e.target.value)}
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>

                                                    {/* Subtotal */}
                                                    <div className="col-md-4 d-flex align-items-end">
                                                        <div className="w-100 text-end">
                                                            <small className="text-muted">
                                                                Subtotal: <strong className="text-primary">{formatCurrency(calcularSubtotal(item))}</strong>
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Valor Total */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label mb-0">Valor Total</label>
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={formData.usar_valor_manual}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            usar_valor_manual: e.target.checked
                                        }))}
                                        disabled={isSubmitting}
                                    />
                                    <label className="form-check-label small">
                                        Editar manualmente
                                    </label>
                                </div>
                            </div>

                            {formData.usar_valor_manual ? (
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.valor_total_manual}
                                    onChange={handleValorTotalChange}
                                    placeholder="0,00"
                                    disabled={isSubmitting}
                                />
                            ) : (
                                <div className="alert alert-secondary mb-0">
                                    <h4 className="mb-0">{formatCurrency(calcularValorTotal())}</h4>
                                    <small className="text-muted">Calculado automaticamente</small>
                                </div>
                            )}
                        </div>

                        {/* Calculadora de Margem (bloco único, aplicado aos produtos em que este orçamento for o menor) */}
                        <div className="card border mb-0">
                            <div className="card-header bg-primary-light">
                                <h6 className="mb-0">Calculadora de Margem</h6>
                                <small className="text-muted">
                                    Aplicada aos produtos em que este orçamento for o de menor valor
                                </small>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Custo Logístico</label>
                                        <div className="input-group">
                                            <span className="input-group-text">R$</span>
                                            <input
                                                type="number"
                                                className="form-control"
                                                step="0.0001"
                                                min="0"
                                                value={custos.custo_logistico}
                                                onChange={(e) => setCustos(prev => ({ ...prev, custo_logistico: e.target.value }))}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Custos Adicionais</label>
                                        <div className="input-group">
                                            <span className="input-group-text">R$</span>
                                            <input
                                                type="number"
                                                className="form-control"
                                                step="0.0001"
                                                min="0"
                                                value={custos.custos_adicionais}
                                                onChange={(e) => setCustos(prev => ({ ...prev, custos_adicionais: e.target.value }))}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">
                                            Imposto <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className={`form-control ${impostoInvalido ? 'is-invalid' : ''}`}
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                value={custos.percentual_imposto}
                                                onChange={(e) => setCustos(prev => ({ ...prev, percentual_imposto: e.target.value }))}
                                                disabled={isSubmitting}
                                            />
                                            <span className="input-group-text">%</span>
                                            {impostoInvalido && (
                                                <div className="invalid-feedback">
                                                    O percentual de imposto é obrigatório
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Margem ao vivo por produto: referência x valor deste orçamento */}
                                {margensPorItem.length > 0 && (
                                    <div className="border-top pt-3">
                                        <small className="text-muted d-block mb-2">
                                            Margem por produto (valor de referência x valor deste orçamento)
                                        </small>
                                        {margensPorItem.map(({ index, nome, margem }) => (
                                            <div key={index} className="d-flex justify-content-between align-items-center py-1">
                                                <span className="small">{nome}</span>
                                                {margem.calculavel ? (
                                                    <span className={`badge ${margemBadgeClass(margem.margemPorcentagem)} rounded-pill`}>
                                                        {margem.margemPorcentagem.toFixed(2)}%
                                                    </span>
                                                ) : (
                                                    <span className="text-muted small">-</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onHide}
                    disabled={isSubmitting}
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isLoading}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            {orcamento ? 'Atualizando...' : 'Salvando...'}
                        </>
                    ) : (
                        <>
                            <i className="ti ti-check me-1" />
                            {orcamento ? 'Atualizar' : 'Salvar Orçamento'}
                        </>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalNovoOrcamento;
